import { createConnectTransport } from "@connectrpc/connect-web";
import { Code, ConnectError, type Interceptor } from "@connectrpc/connect";

// In-memory token storage — tokens are never persisted to localStorage
// to prevent XSS-based token theft.
let accessToken: string | null = null;
let refreshTokenValue: string | null = null;
// Generation counter: incremented on clearTokens() to prevent a concurrent
// refresh from re-storing tokens after logout.
let tokenGeneration = 0;

export function setTokens(access: string, refresh?: string) {
  accessToken = access;
  if (refresh) refreshTokenValue = refresh;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function clearTokens() {
  accessToken = null;
  refreshTokenValue = null;
  tokenGeneration++;
}

export function getRefreshToken(): string | null {
  return refreshTokenValue;
}

export function getTokenGeneration(): number {
  return tokenGeneration;
}

/**
 * Fired when the session is definitively gone — both the in-memory refresh
 * token and the cookie session failed to produce a new access token.
 *
 * An event rather than a direct call into the auth package: this module is what
 * the auth package imports, so reaching back would be a cycle. AuthProvider
 * listens and drops the user, which is what turns an expired session into a
 * sign-in prompt instead of twenty views each claiming "Access restricted".
 */
export const AUTH_EXPIRED_EVENT = "hub:auth-expired";

/**
 * The cookie session's refresh endpoint, mirroring authService.refresh().
 *
 * Read inline rather than imported: authService imports getAccessToken from
 * this module, so importing the other way round would be a cycle. Three
 * duplicated lines are cheaper than that.
 */
function csrfHeader(): Record<string, string> {
  const m = document.cookie.match(/csrf_token=([^;]+)/);
  return m ? { "X-CSRF-Token": m[1] } : {};
}

/**
 * Mint a new access token from the httpOnly cookie session.
 *
 * The SSO flow deliberately keeps NO refresh token in memory — AuthProvider
 * calls setTokens(accessToken, "") for the cookie-based path. Without this
 * fallback the interceptor gave up the instant the 15-minute access token
 * expired, even though the cookie session is good for a week, and every view
 * turned into "Access restricted" until the user reloaded. Reloading worked
 * only because mounting AuthProvider runs exactly this request.
 */
async function refreshFromCookieSession(): Promise<boolean> {
  try {
    const res = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...csrfHeader() },
    });
    if (!res.ok) return false;
    const data = await res.json();
    // The REST endpoint answers in snake_case; the RPC above uses camelCase.
    if (!data.access_token) return false;
    setTokens(data.access_token);
    return true;
  } catch {
    return false;
  }
}

// Deduplicates concurrent token refresh attempts
let refreshPromise: Promise<boolean> | null = null;

async function attemptTokenRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  const generationAtStart = tokenGeneration;

  refreshPromise = (async () => {
    try {
      const refresh = getRefreshToken();
      if (refresh) {
        // Use fetch directly to avoid circular dependency with the transport
        const res = await fetch("/auth.v1.AuthService/RefreshToken", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: refresh }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.accessToken) {
            // If tokens were cleared (logout) while the refresh was in-flight,
            // discard the new tokens to avoid re-authenticating a logged-out session.
            if (tokenGeneration !== generationAtStart) return false;
            setTokens(data.accessToken, data.refreshToken);
            return true;
          }
        }
      }

      // No in-memory refresh token, or it did not work. The cookie session
      // usually still does — it outlives the access token by a week.
      if (tokenGeneration !== generationAtStart) return false;
      if (await refreshFromCookieSession()) {
        return tokenGeneration === generationAtStart;
      }

      // Genuinely signed out. Say so once, globally, rather than letting each
      // view render its own permission-shaped error for what is a sign-in
      // problem with a different remedy.
      clearTokens();
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
      return false;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Interceptor to add auth token to requests and handle 401 responses
const authInterceptor: Interceptor = (next) => async (req) => {
  const token = getAccessToken();
  if (token) {
    req.header.set("Authorization", `Bearer ${token}`);
  }

  try {
    return await next(req);
  } catch (err) {
    if (err instanceof ConnectError && err.code === Code.Unauthenticated) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        const newToken = getAccessToken();
        if (newToken) {
          req.header.set("Authorization", `Bearer ${newToken}`);
        }
        return next(req);
      }
    }
    throw err;
  }
};

// Create the transport for ConnectRPC
// Note: baseUrl is empty because ConnectRPC paths are at the root (e.g., /monitoring.v1.MonitoringService/)
// REST endpoints use /api prefix but ConnectRPC endpoints do not
export const transport = createConnectTransport({
  baseUrl: "",
  interceptors: [authInterceptor],
});

/**
 * The same transport, in protobuf BINARY rather than Connect's JSON default.
 *
 * For everything the workbench sends this makes no practical difference, and
 * JSON stays the default because it is the readable one in devtools — a wire
 * format nobody can eyeball is a debugging tax on every other service.
 *
 * It matters for exactly one thing: a `bytes` field. Connect's JSON codec
 * base64-encodes bytes, so a PDF crosses the wire at 4/3 its real size and the
 * browser then has to JSON.parse and base64-decode EVERY 64KB chunk before a
 * single page can render. On a scanned anesthesia record that is a third more
 * bytes plus main-thread decode work, paid on the reviewer's critical path
 * between clicking a case and seeing the document.
 *
 * Scoped to the blob client rather than flipped globally: the codec is
 * negotiated per-request by Content-Type and the Go handlers accept both, so
 * this is safe, but a study in flight is the wrong time to change the wire
 * format of every RPC at once for a benefit only one of them can collect.
 */
export const binaryTransport = createConnectTransport({
  baseUrl: "",
  interceptors: [authInterceptor],
  useBinaryFormat: true,
});

/**
 * Test seam for attemptTokenRefresh.
 *
 * Exported because the behaviour worth pinning — that an expired access token
 * recovers from the cookie session, and that a dead session says so once — is
 * otherwise only reachable by provoking a real RPC failure through the
 * transport.
 */
export function attemptRefreshForTest(): Promise<boolean> {
  return attemptTokenRefresh();
}
